import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { LoaderBusService } from '../../services/loader-bus/loader-bus.service';

/**
 * A full page loading component that covers the current page in an overlay containing a Material spinner. By default this loader
 * appears when there is activity on the `loader-hub`'s global loading channel, which means that calling the `loader-hub`'s disable
 * function will cause this loader to be hidden, even if there is loading going on in the background.
 *
 * It's possible to inject your own loading content and replace the built in spinner with content projection.
 *
 * **When this overlay is on screen the page and any forms on it are not necessarily disabled and can likely still be interacted with.**
 * (E.g. through the keyboard... tab, enter, etc.) If you'd like to additionally prevent things like form submissions, you should
 * look in to the directives packaged with this library.
 *
 * @example
 * <!-- app.component.html basic top level loader -->
 * <loader-fullpage></loader-fullpage>
 * <router-outlet></router-outlet>
 *
 * <!-- Using a custom channel -->
 * <loader-fullpage channelName="http"></loader-fullpage>
 *
 * <!-- A custom loader that only appears on certain pages -->
 * <loader-fullpage *ngIf="isConfirmPage" >
 * <p>Your order is being processed...</p>
 * </loader-fullpage>
 */
@Component({
    selector: 'of-loader-fullpage',
    templateUrl: './loader-fullpage.component.html',
    styleUrls: ['./loader-fullpage.component.scss'],
    standalone: false
})
export class LoaderFullpageComponent {
    private readonly _loader = inject(LoaderBusService);

    readonly displayLoader = toSignal(
        this._loader.getLoadingStatus('global').pipe(
            map(status => status ? 'flex' : 'none')
        ),
        { initialValue: 'none' }
    );
}
